import { Delete as DeleteIcon } from "@mui/icons-material";
import { IconButton, Table, TableBody, TableCell, TableFooter, TableHead, TableRow, Typography } from "@mui/material";
import type { FC } from "react";
import type { ProductResponse } from "../../admin/products/productsApi";
import type { Currency } from "../../data/BaseEntity";
import ProductOptions from "../../sharedComponents/products/ProductOptions";
import ProductOptionsWithPrice from "../../sharedComponents/products/ProductOptionsWithPrice";
import ProductPrice from "../../sharedComponents/products/ProductPrice";
import CalculationProductName from "../calculationProduct/CalculationProductName";
import type { CalculationProduct, CalculationProductOption } from "../calculationTypes";
import ProductsPriceSummary from "./productsPriceSummary/ProductsPriceSummary";
import type { CalculationProductWithPrices } from "./useProductsWithPrices";

type ProductDetails = Pick<
  ProductResponse,
  "id" | "name" | "options" | "currency" | "description" | "images" | "contact"
>;

interface ProductRowProps {
  readonly rowIndex: number;
  readonly isCustomer: boolean;
  readonly value: CalculationProductWithPrices & { optional: boolean | undefined };
  readonly products: ProductDetails[];
  readonly calculationCurrency: Currency | undefined;
  readonly calculationMargin: number;
  readonly totalParticipantsNumber: number;
  readonly deleteColumnVisible: boolean;
  readonly onChange: (value: CalculationProduct, idx: number) => void;
  readonly onDelete: (idx: number) => void;
  readonly onReplaceProduct: (productId: string, replacement: CalculationProduct) => void;
}

const ProductRow: FC<ProductRowProps> = ({
  rowIndex,
  value,
  products,
  isCustomer,
  calculationCurrency,
  calculationMargin,
  totalParticipantsNumber,
  deleteColumnVisible,
  onChange,
  onDelete,
  onReplaceProduct,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { optional, price, ...calculationProduct } = value;
  const productDetails = products.find((p) => p.id === value.id);
  const onReplace = (replacement: CalculationProduct) => onReplaceProduct(value.id, replacement);

  return (
    <TableRow>
      <TableCell>
        <CalculationProductName
          selectedProduct={value as CalculationProduct}
          productDetails={productDetails}
          calculationCurrency={calculationCurrency}
          calculationMargin={calculationMargin}
          participantsNumber={totalParticipantsNumber}
          onReplace={onReplace}
        >
          <Typography>{productDetails?.name}</Typography>
        </CalculationProductName>
      </TableCell>
      {deleteColumnVisible && (
        <TableCell>
          {optional && (
            <IconButton
              color="secondary"
              onClick={() => onDelete(rowIndex)}
              data-testid="delete-product"
              aria-label="Usuń opcjonalny produkt"
            >
              <DeleteIcon />
            </IconButton>
          )}
        </TableCell>
      )}
      <TableCell>
        <Typography>{value.date}</Typography>
      </TableCell>
      <TableCell>
        {isCustomer ? (
          <ProductOptions
            productId={value.id}
            selectedOptions={value.options}
            availableOptions={productDetails?.options.map((o) => ({ ...o, info: o.info.polish })) ?? []}
            onChange={(options) =>
              onChange({ ...calculationProduct, options: options as CalculationProductOption[] }, rowIndex)
            }
          />
        ) : (
          <ProductOptionsWithPrice
            productId={value.id}
            productDate={value.date}
            selectedOptions={value.options}
            availableOptions={productDetails?.options.map((o) => ({ ...o, info: o.info.polish })) ?? []}
            onChange={(options) =>
              onChange({ ...calculationProduct, options: options as CalculationProductOption[] }, rowIndex)
            }
          />
        )}
      </TableCell>
      {!isCustomer && (
        <TableCell>
          {calculationCurrency && <ProductPrice price={value.price} currency={calculationCurrency} />}
        </TableCell>
      )}
    </TableRow>
  );
};

interface Props {
  readonly isCustomer: boolean;
  readonly selectedProducts: (CalculationProductWithPrices & { optional: boolean | undefined })[];
  readonly availableProducts: ProductDetails[];
  readonly calculationCurrency: Currency | undefined;
  readonly totalParticipantsNumber?: number;
  readonly paidParticipantsNumber: number;
  readonly margin: number;
  readonly onChange: (value: CalculationProduct, idx: number) => void;
  readonly onDelete: (idx: number) => void;
  readonly onReplaceProduct: (productId: string, replacement: CalculationProduct) => void;
}

const CalculationPackageProducts: FC<Props> = ({
  isCustomer,
  selectedProducts,
  availableProducts,
  calculationCurrency,
  totalParticipantsNumber,
  paidParticipantsNumber,
  margin,
  onChange,
  onDelete,
  onReplaceProduct,
}) => {
  const hasOptionalProducts = selectedProducts.some((p) => p.optional);
  return (
    <Table data-testid="calculation-products">
      <TableHead>
        <TableRow>
          <TableCell>Produkt</TableCell>
          {hasOptionalProducts && <TableCell />}
          <TableCell>Data</TableCell>
          <TableCell>Opcje</TableCell>
          {!isCustomer && <TableCell>Cena</TableCell>}
        </TableRow>
      </TableHead>
      <TableBody>
        {selectedProducts.map((p, idx) => (
          <ProductRow
            key={`${p.id}#${p.date}`}
            rowIndex={idx}
            value={p}
            products={availableProducts}
            isCustomer={isCustomer}
            calculationCurrency={calculationCurrency}
            calculationMargin={margin}
            totalParticipantsNumber={totalParticipantsNumber ?? 0}
            deleteColumnVisible={hasOptionalProducts}
            onChange={onChange}
            onDelete={onDelete}
            onReplaceProduct={onReplaceProduct}
          />
        ))}
      </TableBody>
      {!isCustomer && (
        <TableFooter>
          <TableRow>
            <TableCell />
            <TableCell />
            <TableCell colSpan={2}>
              <ProductsPriceSummary
                calculationProducts={selectedProducts}
                availableProducts={availableProducts}
                currency={calculationCurrency}
                margin={margin}
                numberOfPeople={paidParticipantsNumber}
              />
            </TableCell>
          </TableRow>
        </TableFooter>
      )}
    </Table>
  );
};

export default CalculationPackageProducts;
